import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { XCircle, Upload, Mail, RefreshCw } from 'lucide-react';

const PaymentRejected = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { payment } = location.state || {};

  if (!payment) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center py-8 px-4">
      <Card className="max-w-2xl w-full shadow-2xl">
        <CardContent className="p-8">
          {/* Rejection Icon */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">❌ PAYMENT VERIFICATION FAILED</h1>
            <p className="text-lg text-gray-600">
              We couldn't verify your payment.
            </p>
          </div>

          <div className="border-t border-b border-gray-200 py-4 my-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="text-lg font-bold text-gray-900">{payment.order_id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Reviewed</p>
                <p className="font-semibold">{new Date(payment.verified_at).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Rejection Reason */}
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertDescription>
              <p className="font-bold text-red-900 mb-2">REASON FOR REJECTION:</p>
              <div className="bg-white rounded p-3 text-red-800">
                <p className="flex items-start">
                  <span className="text-xl mr-2">⚠️</span>
                  <span>{payment.rejection_reason || 'Payment verification failed. Please check your transfer details and try again.'}</span>
                </p>
              </div>
            </AlertDescription>
          </Alert>

          {/* What to Do */}
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <h3 className="font-bold text-orange-900 mb-3">WHAT TO DO:</h3>
              
              <div className="space-y-4">
                <div className="bg-white rounded p-3">
                  <p className="font-semibold text-gray-900 mb-2">Option 1: Upload New Proof</p>
                  <ul className="text-sm text-gray-700 space-y-1 ml-4">
                    <li>• Check your transfer details</li>
                    <li>• Ensure correct amount was transferred</li>
                    <li>• Upload a clear photo of bank slip</li>
                  </ul>
                </div>

                <div className="bg-white rounded p-3">
                  <p className="font-semibold text-gray-900 mb-2">Option 2: Contact Support</p>
                  <ul className="text-sm text-gray-700 space-y-1 ml-4">
                    <li>• Email: payments@zeilian.co</li>
                    <li>• Phone: +94 11 234 5678</li>
                    <li>• We'll help resolve this issue</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/payment/upload-proof', { 
                state: { orderId: payment.order_id, amount: payment.amount, serviceTitle: payment.service_type } 
              })}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
            >
              <Upload className="h-5 w-5 mr-2" />
              Upload New Proof
            </Button>
            
            <Button
              variant="outline"
              className="w-full py-6"
              onClick={() => window.location.href = 'mailto:payments@zeilian.co?subject=Payment Issue - Order ' + payment.order_id}
            >
              <Mail className="h-4 w-4 mr-2" />
              Contact Support
            </Button>

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Help Message */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg text-center">
            <p className="text-sm text-blue-900">
              <strong>👥 We're here to help!</strong> Most payment issues can be resolved quickly. 
              Our team is available to assist you.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentRejected;